import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import { FESTIVALS } from "@/data/festivals";
import { useSeo } from "@/lib/seo";
import { StotraHeader } from "../stotras/StotraHub";

const monthOf = (d) => new Date(d + "T00:00:00").toLocaleString("en-IN", { month: "short" });
const dayOf = (d) => new Date(d + "T00:00:00").getDate();

export default function FestivalHub() {
  const YEARS = [2026, 2027];
  const today0 = new Date(); today0.setHours(0, 0, 0, 0);
  const defaultYear = YEARS.find((y) => FESTIVALS.some((f) => f.year === y && new Date(f.date + "T00:00:00") >= today0)) || YEARS[YEARS.length - 1];
  const [year, setYear] = useState(defaultYear);
  useSeo({
    title: "Hindu Festival Calendar 2026 & 2027 — ಹಬ್ಬಗಳ ಪಂಚಾಂಗ | Dates, Pooja Vidhi & Stotras | PurohithConnect",
    description: "Hindu festival dates for 2026 and 2027 with pooja vidhi and stotras — Maha Shivaratri, Ugadi, Varamahalakshmi, Ganesha Chaturthi, Navaratri, Deepavali and more. Kannada and English.",
    canonicalPath: "/festivals",
    jsonLd: {
      "@context": "https://schema.org", "@type": "ItemList",
      name: "Hindu Festival Calendar 2026 & 2027",
      itemListElement: FESTIVALS.map((f, i) => ({
        "@type": "ListItem", position: i + 1, name: `${f.nameEn} — ${f.dateText}`,
        url: `https://purohithconnect.com/festivals/${f.slug}`,
      })),
    },
  });

  const shown = FESTIVALS.filter((f) => f.year === year);

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]">
      <StotraHeader />
      <main className="mx-auto max-w-5xl px-4 pb-20 md:px-8">
        <section className="pt-10 text-center">
          <p className="font-baloo text-base text-[#B23A2B]">ಹಬ್ಬಗಳ ಪಂಚಾಂಗ</p>
          <h1 className="mt-1 font-eczar text-4xl font-bold sm:text-5xl" data-testid="festival-hub-title">Festival Calendar</h1>
          <p className="mx-auto mt-3 max-w-2xl font-mukta text-base text-[#6B5044]">
            Dates, significance, pooja vidhi and the right stotras for every major festival — in Kannada and English.
          </p>
          <div className="mt-5 inline-flex rounded-full border border-[#A8722A]/30 bg-[#FFFDF8] p-1" data-testid="festival-year-toggle">
            {[2026, 2027].map((y) => (
              <button key={y} onClick={() => setYear(y)} data-testid={`festival-year-${y}`}
                className={`rounded-full px-5 py-1.5 font-mukta text-sm font-semibold transition-colors ${year === y ? "bg-[#B23A2B] text-white" : "text-[#6B5044] hover:text-[#3B221B]"}`}>
                {y}
              </button>
            ))}
          </div>
        </section>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((f) => (
            <Link key={f.slug} to={`/festivals/${f.slug}`} data-testid={`festival-card-${f.slug}`}
              className="group overflow-hidden rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] shadow-sm transition-shadow hover:shadow-md">
              <div className="h-44 overflow-hidden">
                <img src={f.img} alt={f.nameEn} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
              </div>
              <div className="flex items-start gap-3 p-4">
                <div className="shrink-0 rounded-lg border border-[#B23A2B]/30 px-2.5 py-1 text-center" style={{ backgroundColor: "rgba(178,58,43,0.06)" }}>
                  <p className="font-mukta text-[10px] font-bold uppercase text-[#B23A2B]">{monthOf(f.date)}</p>
                  <p className="font-eczar text-lg font-bold leading-none text-[#3B221B]">{dayOf(f.date)}</p>
                </div>
                <div>
                  <h2 className="font-eczar text-lg font-semibold leading-snug">{f.nameEn}</h2>
                  <p className="font-baloo text-sm text-[#A8722A]">{f.nameKn}</p>
                  <p className="mt-1 flex items-center gap-1 font-mukta text-xs text-[#6B5044]"><CalendarDays size={12} /> {f.dateText}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </main>
      <footer className="border-t border-[#A8722A]/20 bg-[#F3EADA] py-5 text-center font-mukta text-sm text-[#6B5044]">
        © 2026 PurohithConnect · Built in Bengaluru, Karnataka — for India
      </footer>
    </div>
  );
}
